import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Trash, Spinner } from '@phosphor-icons/react';
import { useLanguage } from '../../context/LanguageContext';
import { useModalTrap } from './useModalTrap';

/* ──────────────────────────────────────────
   Shared destructive-action confirm dialog.
   Themed replacement for window.confirm:
   translated, focus-managed, Escape-aware.
   ────────────────────────────────────────── */
export default function ConfirmModal({
  title,
  message,
  confirmLabel,
  busyLabel,
  onConfirm,
  onClose,
  busy = false,
}) {
  const { t } = useLanguage();
  const confirmRef = useRef(null);
  const dialogRef = useRef(null);
  useModalTrap(dialogRef, onClose, !busy);

  useEffect(() => {
    confirmRef.current?.focus();
  }, []);

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/45 backdrop-blur-sm animate-[fade-in_150ms_ease-out]" onClick={() => { if (!busy) onClose(); }}>
      <style>{`
        @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
        @keyframes modal-pop-in { from { opacity: 0; transform: scale(0.96); } to { opacity: 1; transform: scale(1); } }
      `}</style>
      <div
        ref={dialogRef}
        className="bg-white rounded-2xl shadow-ft-lg w-full max-w-md overflow-hidden animate-[modal-pop-in_200ms_ease-out]"
        onClick={e => e.stopPropagation()}
        role="alertdialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <h2 className="text-lg font-extrabold text-ft-text-1">{title}</h2>
          <button onClick={onClose} disabled={busy} aria-label={t('common.close')} className="text-ft-text-3 hover:text-ft-text-1 transition-colors p-1 rounded-lg hover:bg-slate-100 disabled:opacity-50">
            <X />
          </button>
        </div>
        <div className="p-5 space-y-5">
          <p className="text-sm text-ft-text-2">{message}</p>
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={onClose}
              disabled={busy}
              className="px-4 py-2 rounded-lg border border-slate-200 text-ft-text-2 text-sm font-bold hover:bg-ft-surface-2 transition-colors disabled:opacity-50"
            >
              {t('common.cancel')}
            </button>
            <button
              ref={confirmRef}
              onClick={onConfirm}
              disabled={busy}
              className="px-4 py-2 rounded-lg bg-red-500 text-white text-sm font-bold hover:bg-red-600 disabled:opacity-60 transition-colors flex items-center gap-2"
            >
              {busy ? <><Spinner className="animate-spin" /> {busyLabel || t('common.saving')}</> : <><Trash /> {confirmLabel || t('common.delete')}</>}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
